import { AdjustmentsHorizontalIcon, ClockIcon, RectangleStackIcon, SparklesIcon, Square3Stack3DIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useState } from 'react';
import { TabPane, TabsContainer } from '../../../components/Tabs';
import { ViewButton } from '../../../components/ViewSelector';
import { T } from '../../../translation/src';
import { AiAssistPanel } from '../ai/AiAssistPanel';
import { useObjectEditor, type EditorView } from '../EditorContext';
import { HistoryPanel } from '../inspector/HistoryPanel';
import { Inspector } from '../inspector/Inspector';
import { LayersPanel } from '../panels/LayersPanel';
import { ParamsPanel } from '../panels/ParamsPanel';
import { RulesPanel } from '../panels/RulesPanel';

/** Project side-panel chrome: grey column, tab row, scrolling content. */
const panelClass = 'relative flex h-full w-80 flex-shrink-0 flex-col overflow-hidden border-t border-gray-200 bg-[rgb(230,230,230)] px-3 py-3 dark:border-transparent dark:bg-[rgb(33,33,33)] dark:text-white';

type LeftTab = 'layers' | 'parameters';

/** The smart panel's only tab: nothing to switch to. */
const noTabChange = (): void => undefined;

/** A smart object's left panel: the AI designer instead of layers and parameters. */
const SmartLeftPanel: React.FC = () => (
  <div data-panel="left" className={panelClass}>
    <TabsContainer defaultActiveKey="ai" onChange={noTabChange} tabWidth="9rem">
      <TabPane key="ai">
        <div className="inline-flex select-none items-center space-x-2">
          <SparklesIcon className="h-5 w-5" />
          <div>
            <T _str="smart designer" swc />
          </div>
        </div>
      </TabPane>
    </TabsContainer>
    <div className="-mx-3 mt-2 min-h-0 flex-1 overflow-y-auto">
      <AiAssistPanel />
    </div>
  </div>
);

/** Left panel: the drawing's layers (groups + shapes) and the object's parameters, as tabs (a smart object: the AI designer). */
const EditorLeftPanel_: React.FC = () => {
  const { definition } = useObjectEditor();
  const [tab, setTab] = useState<LeftTab>('layers');
  if (definition.smart) return <SmartLeftPanel />;
  return (
    <div data-panel="left" className={panelClass}>
      <TabsContainer defaultActiveKey={tab} onChange={({ key }) => setTab(key === 'parameters' ? 'parameters' : 'layers')} tabWidth="9rem">
        <TabPane key="layers">
          <div className="inline-flex select-none items-center space-x-2">
            <RectangleStackIcon className="h-5 w-5" />
            <div>
              <T _str="layers" swc />
            </div>
          </div>
        </TabPane>
        <TabPane key="parameters">
          <div className="inline-flex select-none items-center space-x-2">
            <AdjustmentsHorizontalIcon className="h-5 w-5" />
            <div>
              <T _str="parameters" swc />
            </div>
          </div>
        </TabPane>
      </TabsContainer>
      <div className="-mx-3 mt-2 min-h-0 flex-1 overflow-y-auto">
        {tab === 'layers' ? (
          <LayersPanel />
        ) : (
          <>
            <ParamsPanel />
            <RulesPanel />
          </>
        )}
      </div>
    </div>
  );
};
export const EditorLeftPanel = memo(EditorLeftPanel_);

type RightTab = 'properties' | 'history';

const rightTabOf = (key: unknown): RightTab => (key === 'history' ? key : 'properties');

/**
 * Right panel: properties of the selection (the object itself when nothing is selected) or Test mode's placement, and
 * the published versions.
 */
const EditorRightPanel_: React.FC = () => {
  const [tab, setTab] = useState<RightTab>('properties');
  return (
    <div data-panel="right" className={panelClass}>
      <TabsContainer defaultActiveKey={tab} onChange={({ key }) => setTab(rightTabOf(key))} tabWidth="9rem">
        <TabPane key="properties">
          <div className="inline-flex select-none items-center space-x-2">
            <Square3Stack3DIcon className="h-5 w-5" />
            <div>
              <T _str="properties" swc />
            </div>
          </div>
        </TabPane>
        <TabPane key="history">
          <div className="inline-flex select-none items-center space-x-2">
            <ClockIcon className="h-5 w-5" />
            <div>
              <T _str="history" swc />
            </div>
          </div>
        </TabPane>
      </TabsContainer>
      <div className="-mx-3 mt-2 min-h-0 flex-1 overflow-y-auto">
        {tab === 'history' && <HistoryPanel />}
        {tab === 'properties' && <Inspector />}
      </div>
    </div>
  );
};
export const EditorRightPanel = memo(EditorRightPanel_);

const VIEWS: ReadonlyArray<{ id: EditorView; label: React.ReactNode }> = [
  { id: '2d', label: '2D' },
  { id: 'split', label: <T _str="2D + 3D" /> },
  { id: '3d', label: '3D' },
];

/** The project's view switch (bottom-left corner). */
const EditorViewSwitch_: React.FC<{ className?: string }> = ({ className }) => {
  const { ui, setUi } = useObjectEditor();
  return (
    <div className={classNames('z-10 flex w-auto gap-px', className)}>
      {VIEWS.map((view, index) => (
        <ViewButton key={view.id} label={view.label} active={ui.view === view.id} onClick={() => setUi((u) => ({ ...u, view: view.id }))} className={classNames({ 'rounded-l-lg': index === 0, 'rounded-r-lg': index === VIEWS.length - 1 })} />
      ))}
    </div>
  );
};
export const EditorViewSwitch = memo(EditorViewSwitch_);
